import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, FormHelperText, Typography, useTheme } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import type { MovementError } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import type { Movement } from "@services/movement-services/types";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import z from "zod";
import { useCompleteMovement } from "../hooks/useCompleteMovement";

// This form does not have a store defined, so all fields will be managed via local state or props
// => use regular input components and manage their state locally or via props
interface RejectMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}
interface OutletContextType {
  vesselCallDetails: { id: string; vesselId: string };
}

const serviceVesselCall = "bff-vessel-call";
const RejectMovementService: React.FC<RejectMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const { userAccessPermissions } = useAuthStore();
  const queryClient = useQueryClient();
  const { vesselCallDetails } = useOutletContext<OutletContextType>();
  const vesselId = vesselCallDetails?.vesselId;
  const vesselCallId = vesselCallDetails?.id;

  // Check if user has permission to view internal notes
  const hasInternalNotesAccess = checkPermission(userAccessPermissions, "hasInternalNotesAccess");
  const currentMovement = currentMovements?.[0];
  const { t } = useTranslation();
  const [internalNotes, setInternalNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [error, setError] = useState<null | string>(null);
  const [internalAttachmentsUrl, setInternalAttachmentsUrl] = useState<string>();
  const [rejectAttachmentsUrl, setRejectAttachmentsUrl] = useState<string>();
  const [uploadingDocumentType, setUploadingDocumentType] = useState<string | null>(null);
  const [additionalErrors, setAdditionalErrors] = useState<{
    internalNotes?: string;
    rejectReason?: string;
    files?: string;
  }>({});
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const TEXTAREA_MAX_CHARS = 1000;
  const theme = useTheme();
  const internalNotesOnlySchema = z.object({
    internalNotes: z
      .string()
      .max(1000, t("form-validation.max-length", { label: t("form-validation.field"), max: 1000 }))
      .optional(),
  });
  const rejectReasonSchema = z.object({
    rejectReason: z
      .string()
      .min(
        1,
        t("form-validation.required", {
          label: t("vessel-management.vessel-status-page.externalNotes"),
        }),
      )
      .max(
        1000,
        t("form-validation.max-length", {
          label: t("vessel-management.vessel-status-page.externalNotes"),
          max: 1000,
        }),
      ),
  });
  const inputSlotProps = {
    root: {
      className: `rounded-lg text-sm px-4 py-2 border`,
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: {
      maxLength: TEXTAREA_MAX_CHARS,
    },
  };

  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": {
      padding: 0,
    },
  };
  const styles = getSideDrawerStyles(theme);
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const rejectAttachmentFile = useUploadStore(
    (state) => state.filesByKey.rejectAttachmentFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const internalAttachmentsFile = useUploadStore(
    (state) => state.filesByKey.internalAttachmentsFile ?? EMPTY_UPLOAD_ENTRIES,
  );
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };
  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;

      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);

      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );
  const { notify } = useNotification();
  const rejectUploadSignatureRef = useRef<string | null>(null);
  const internalUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value: { files?: string } | ((prev: { files?: string }) => { files?: string }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ files: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };
  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "RejectMovementService",
          action: "upload_movement_service_document",
          entityType: "movement_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.VESSEL_REGISTRATION,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType };
        },
      ),
    onError: (error: Error) => setError(error.message),
    onSettled: () => setUploadingDocumentType(null),
  });
  const setSideDrawerAdditionalFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("sideDrawerAdditionalFile");
        return;
      }
      setFilesForKey("sideDrawerAdditionalFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const setRejectAttachmentFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("rejectAttachmentFile");
        return;
      }
      setFilesForKey("rejectAttachmentFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const setInternalAttachmentsFile = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("internalAttachmentsFile");
        return;
      }
      setFilesForKey("internalAttachmentsFile", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  useEffect(() => {
    setSideDrawerAdditionalFile([]);
    setRejectAttachmentFile([]);
    setInternalAttachmentsFile([]);
  }, []);

  const resetFormValues = () => {
    setRejectReason("");
    setInternalNotes("");
    setAdditionalErrors({});
    setRejectAttachmentsUrl("");
    setInternalAttachmentsUrl("");
    setRejectAttachmentFile([]);
    setInternalAttachmentsFile([]);
    setSideDrawerAdditionalFile([]);
  };

  const handleDocumentUpload = (file: File, documentType: string) => {
    setUploadingDocumentType(documentType);
    documentUploadMutation.mutate(
      { file, documentType },
      {
        onSuccess: ({
          documentUrl,
          documentType: currentType,
        }: {
          documentUrl: string;
          documentType: string;
        }) => {
          setError(null);
          if (currentType === "INTERNAL_NOTES") {
            setInternalAttachmentsUrl(documentUrl);
          } else if (currentType === "REJECT_NOTES") {
            setRejectAttachmentsUrl(documentUrl);
          }
        },
      },
    );
  };
  const handleSuccess = async () => {
    setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });

      // Invalidate and refetch the preview
      const movementServiceId = currentMovement.id;
      if (movementServiceId) {
        await queryClient.invalidateQueries({
          queryKey: ["movement-service-preview", movementServiceId],
        });
        await queryClient.refetchQueries({
          queryKey: ["movement-service-preview", movementServiceId],
        });
      }
    }, 2000);
    onClose("rejectDrawer");
    resetFormValues();
    notify({
      title: t("vessel-call.movement-services.movement-rejected"),
      type: "success",
    });
  };

  const handleError = async (error: MovementError) => {
    await queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });

    // Invalidate and refetch the preview
    const movementServiceId = currentMovement.id;
    if (movementServiceId) {
      await queryClient.invalidateQueries({
        queryKey: ["movement-service-preview", movementServiceId],
      });
      await queryClient.refetchQueries({
        queryKey: ["movement-service-preview", movementServiceId],
      });
    }

    onClose("rejectDrawer");
    resetFormValues();
    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
  };

  const { completeMovement } = useCompleteMovement({
    handleSuccess,
    handleError,
  });

  const handleSubmit = async () => {
    if (documentUploadMutation.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    const validation = rejectReasonSchema.safeParse({ rejectReason });
    if (!validation.success) {
      setAdditionalErrors((prev) => ({
        ...prev,
        rejectReason: validation.error.flatten().fieldErrors.rejectReason?.[0],
      }));
      return;
    }
    const payload: {
      status: "REJECTED";
      rejectionReason?: string;
      rejectionReasonAttachments?: [
        {
          originalFileName: string;
          url: string;
          type: string;
        },
      ];
      internalNotes?: string;
      internalAttachments?: [
        {
          originalFileName: string;
          url: string;
          type: string;
        },
      ];
    } = {
      status: "REJECTED",
    };
    if (hasInternalNotesAccess) {
      const trimmedInternalNotes = internalNotes?.trim();
      payload.internalNotes = trimmedInternalNotes || undefined;
      if (internalAttachmentsUrl?.length) {
        payload.internalAttachments = [
          {
            originalFileName: internalAttachmentsFile[0]?.name || "attachment",
            url: internalAttachmentsUrl,
            type: "INTERNAL_ATTACHMENTS",
          },
        ];
      }
    }

    // Always include the rejection reason (already validated)
    payload.rejectionReason = rejectReason;
    if (rejectAttachmentsUrl?.length) {
      payload.rejectionReasonAttachments = [
        {
          originalFileName: rejectAttachmentFile[0]?.name || "attachment",
          url: rejectAttachmentsUrl,
          type: "REJECT_ATTACHMENTS",
        },
      ];
    }

    await completeMovement({
      id: currentMovement.id || "",
      service: serviceVesselCall,
      payload,
    });
  };

  return (
    <div data-testid="reject-movement-service-form" className="max-w-sm">
      <SideDrawer
        isOpen={isOpen}
        title={t(`vessel-call.movement-services.reject-title`)}
        onClose={() => {
          onClose("rejectDrawer");
          resetFormValues();
        }}
        submitLabel={t(`vessel-call.movement-services.reject-button`)}
        onSubmit={handleSubmit}
      >
        <div className="flex-1 max-w-sm">
          <div className="px-6 pb-4">
            <Typography
              variant="body1"
              data-testid="movement-service-form-title"
              sx={{ color: "text.primary", pb: 4 }}
            >
              {t(`vessel-call.movement-services.reject-subtitle`)}
            </Typography>

            {/* Reject Reason */}
            <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
              <CustomFormLabel>
                {t("vessel-management.vessel-status-page.externalNotes")}
              </CustomFormLabel>
              <Input
                placeholder={t("vessel-management.vessel-status-page.externalNotes")}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                value={rejectReason}
                onKeyDown={(e) => {
                  if (!isPrintableKey(e)) return;
                  if (rejectReason.length >= TEXTAREA_MAX_CHARS) {
                    setAdditionalErrors((prev) => ({
                      ...prev,
                      rejectReason: t("form-validation.max-length", {
                        label: t("vessel-management.vessel-status-page.externalNotes"),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  setAdditionalErrors((prev) => ({
                    ...prev,
                    additionalDescription: undefined,
                  }));
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setRejectReason(value);
                  const result = rejectReasonSchema.safeParse({
                    rejectReason: value,
                  });
                  setAdditionalErrors((prev) => ({
                    ...prev,
                    rejectReason: result.success
                      ? undefined
                      : result.error.flatten().fieldErrors.rejectReason?.[0],
                  }));
                }}
                slotProps={inputSlotProps}
                sx={sxProps}
                testId={"additional-notes-input"}
              />
              {additionalErrors.rejectReason && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {additionalErrors.rejectReason}
                </p>
              )}
              <FormHelperText sx={charCounterSx}>
                {rejectReason.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
            </div>

            {/* Rejection Attachments */}
            <div className="col-span-full mt-6 ">
              <input type="hidden" name="rejectAttachmentFileUrl" />
              <div className={additionalErrors.files ? "border border-red-500 rounded-md p-1" : ""}>
                <DocumentUpload
                  label={t("vessel-management.vessel-status-page.externalAttachments")}
                  testId={"additional-file-upload"}
                  name="rejectAttachmentFile"
                  isSideDrawer
                  maxFiles={1}
                  allowedExtensions={allowedSideDrawerExtensions}
                  onUpload={(newFiles) =>
                    handleFileUpload({
                      newFiles,
                      setUploadErrors: setAdditionalErrors,
                      setUploadFiles: setRejectAttachmentFile,
                      onUploadDocument: (file) => handleDocumentUpload(file, "REJECT_NOTES"),
                      uploadTracker: rejectUploadSignatureRef,
                    })
                  }
                  conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                  files={rejectAttachmentFile}
                  setFiles={setRejectAttachmentFile}
                  uploadDescription={t("upload.dragOrDropDescription")}
                />
              </div>
              {additionalErrors.files && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {additionalErrors.files}
                </p>
              )}

              {rejectAttachmentFile[0] && !additionalErrors.files && (
                <div className="flex-1 mt-4">
                  <SingleFile
                    file={rejectAttachmentFile[0]}
                    fieldName="rejectAttachmentFile"
                    isLoading={
                      documentUploadMutation.isPending && uploadingDocumentType === "REJECT_NOTES"
                    }
                    setFiles={() => {
                      setAdditionalErrors((prev) => ({ ...prev, files: undefined }));
                      setRejectAttachmentFile([]);
                      rejectUploadSignatureRef.current = null;
                    }}
                  />
                </div>
              )}
            </div>

            {/* Internal Notes & Attachments (permission-based) */}
            {hasInternalNotesAccess && (
              <>
                {/* Internal Notes */}
                <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
                  <CustomFormLabel>
                    {t("vessel-management.vessel-status-page.internalNotes")}
                  </CustomFormLabel>
                  <Input
                    placeholder={t("vessel-management.vessel-status-page.internalNotes")}
                    multiline
                    minRows={4}
                    fullWidth
                    variant="outlined"
                    value={internalNotes}
                    onKeyDown={(e) => {
                      if (!isPrintableKey(e)) return;
                      if (internalNotes.length >= TEXTAREA_MAX_CHARS) {
                        setAdditionalErrors((prev) => ({
                          ...prev,
                          internalNotes: t("form-validation.max-length", {
                            label: t("vessel-management.vessel-status-page.internalNotes"),
                            max: TEXTAREA_MAX_CHARS,
                          }),
                        }));
                      }
                    }}
                    onBlur={() => {
                      setAdditionalErrors((prev) => ({
                        ...prev,
                        additionalDescription: undefined,
                      }));
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setInternalNotes(value);
                      const result = internalNotesOnlySchema.safeParse({
                        internalNotes: value,
                      });
                      setAdditionalErrors((prev) => ({
                        ...prev,
                        internalNotes: result.success
                          ? undefined
                          : result.error.flatten().fieldErrors.internalNotes?.[0],
                      }));
                    }}
                    slotProps={inputSlotProps}
                    sx={sxProps}
                    testId={"additional-notes-input"}
                  />
                  {additionalErrors.internalNotes && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {additionalErrors.internalNotes}
                    </p>
                  )}
                  <FormHelperText sx={charCounterSx}>
                    {internalNotes.length}/{TEXTAREA_MAX_CHARS}
                  </FormHelperText>
                </div>

                {/* Internal Attachments */}
                <div className="col-span-full mt-6 ">
                  <input type="hidden" name="sideDrawerAdditionalFileUrl" />
                  <div
                    className={additionalErrors.files ? "border border-red-500 rounded-md p-1" : ""}
                  >
                    <DocumentUpload
                      label={t("vessel-management.vessel-status-page.internalAttachments")}
                      testId={"additional-file-upload"}
                      name="internalAttachmentsFile"
                      isSideDrawer
                      maxFiles={1}
                      allowedExtensions={allowedSideDrawerExtensions}
                      onUpload={(newFiles) =>
                        handleFileUpload({
                          newFiles,
                          setUploadErrors: setAdditionalErrors,
                          setUploadFiles: setInternalAttachmentsFile,
                          onUploadDocument: (file) => handleDocumentUpload(file, "INTERNAL_NOTES"),
                          uploadTracker: internalUploadSignatureRef,
                        })
                      }
                      conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                      files={internalAttachmentsFile}
                      setFiles={setInternalAttachmentsFile}
                      uploadDescription={t("upload.dragOrDropDescription")}
                    />
                  </div>
                  {additionalErrors.files && (
                    <p style={styles.errorText} className="text-sm mt-1">
                      {additionalErrors.files}
                    </p>
                  )}

                  {internalAttachmentsFile[0] && !additionalErrors.files && (
                    <div className="flex-1 mt-4">
                      <SingleFile
                        file={internalAttachmentsFile[0]}
                        fieldName="internalAttachmentsFile"
                        isLoading={
                          documentUploadMutation.isPending &&
                          uploadingDocumentType === "INTERNAL_NOTES"
                        }
                        setFiles={() => {
                          setAdditionalErrors((prev) => ({ ...prev, files: undefined }));
                          setInternalAttachmentsFile([]);
                          internalUploadSignatureRef.current = null;
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </SideDrawer>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default RejectMovementService;
